import { PLATFORMS } from "@/utils/constants";
import { Dropdown } from "antd";
import { useTranslation } from "react-i18next";

export const SystemsDropDown = (props) => {
  const { onChange, value, allPlatforms, ...restProps } = props;
  const { t } = useTranslation();

  // Ant Design Dropdown requires menu.items so the overlay is a single element; without this,
  // Groups and other callers that omit allPlatforms would pass items: undefined and crash.
  const platformEntries = allPlatforms ?? Object.entries(PLATFORMS);

  const menuItems = platformEntries.map(([platformKey, platformData]) => {
    const icon =
      platformKey === value ? platformData?.selectedIcon : platformData?.icon;

    return {
      key: platformKey,
      label: (
        <div className="d-flex align-items-center gap-2">
          {icon}
          <span className="platform-text-truncate">{t(platformData.name)}</span>
        </div>
      ),
      onClick: () => onChange(platformKey),
      style:
        platformKey === value
          ? {
              backgroundColor: "#faf8f6",
              color: "#7a4819",
            }
          : undefined,
    };
  });

  // Create menu component
  const menu = {
    items: menuItems,
  };

  const currentPlatform = PLATFORMS[value];

  return (
    <div className="--dropdown-list">
      <Dropdown
        {...restProps}
        menu={menu}
        trigger={["click"]}
        placement="bottomLeft"
        overlayClassName="platform-select-dropdown"
      >
        <div className="platform-select-trigger">
          <div className="d-flex align-items-center gap-2">
            {/* Show fallback text when platform is not in config */}
            {currentPlatform ? (
              <>
                {currentPlatform.icon}
                <span className="platform-text-truncate">
                  {t(currentPlatform.name)}
                </span>
              </>
            ) : (
              <span className="platform-text-truncate">
                {t("SELECT_A_PLATFORM")}
              </span>
            )}
          </div>
        </div>
      </Dropdown>

      <style jsx>{`
        .platform-text-truncate {
          max-width: 180px; /* Account for icon width and gap */
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          flex: 1;
        }

        .platform-select-trigger {
          border: 1px solid #e5e4e2 !important;
          border-radius: 1rem !important;
          padding: 0.75rem !important;
          min-height: auto !important;
          background-color: inherit !important;
          font-weight: 500 !important;
          font-size: 0.875rem !important;
          line-height: 1rem !important;
          color: #000 !important;
          cursor: pointer;
          transition: border-color 0.2s ease;
          max-width: 228px;
        }

        .platform-select-trigger:hover {
          border-color: #e5e4e2 !important;
        }

        .platform-select-trigger:focus {
          border-color: #e5e4e2 !important;
          box-shadow: none !important;
          outline: none;
        }

        :global(.platform-select-dropdown .ant-dropdown) {
          border-radius: 1rem !important;
          border: 1px solid #e5e4e2 !important;
          animation: dropdownSlideIn 150ms ease-in-out forwards !important;
          background: white !important;
          overflow: auto !important;
        }

        :global(.platform-select-dropdown .ant-dropdown-menu) {
          border-radius: 1rem !important;
          border: 1px solid #e5e4e2 !important;
          background: white !important;
          padding: 0 !important;
        }

        :global(.platform-select-dropdown .ant-dropdown-menu-item) {
          padding: 16px !important;
          font-weight: 500 !important;
          font-size: 0.875rem !important;
          line-height: 1rem !important;
          color: #000 !important;
          border-radius: 0 !important;
        }

        :global(.platform-select-dropdown .ant-dropdown-menu-item:first-child) {
          border-top-left-radius: 1rem !important;
          border-top-right-radius: 1rem !important;
        }

        :global(.platform-select-dropdown .ant-dropdown-menu-item:last-child) {
          border-bottom-left-radius: 1rem !important;
          border-bottom-right-radius: 1rem !important;
        }

        :global(.platform-select-dropdown .ant-dropdown-menu-item-selected) {
          background-color: #faf8f6 !important;
          color: #7a4819 !important;
        }

        :global(.platform-select-dropdown .ant-dropdown-menu-item:hover) {
          background-color: #f4f6fd !important;
          color: #7a4819 !important;
        }

        @keyframes dropdownSlideIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};
